/**
 * The browser tab: document title, favicon, and the Open Graph pair, on
 * storefront routes only.
 *
 * Jake, 2026-09-19, on whether the tab and favicon were in scope for
 * white-labeling: "Handle these also."
 *
 * WHY THIS IS A RUNTIME HOOK AND NOT AN EDIT TO index.html
 * --------------------------------------------------------
 * `frontend/index.html` is STATIC and GLOBAL -- one file for the entire SPA,
 * served identically to admin, distributor, superadmin and storefront. It
 * carries `<title>ThreadFlow</title>`, an og:title/og:description pair, and
 * `<link rel="icon" href="/static/images/redOWfavicon.png">`. There is no
 * per-request templating of it, so it cannot express a per-store value at all.
 *
 * So those tags stay exactly as they are and become the DEFAULT. This module
 * injects over them when a white-labeled storefront is on screen, and puts
 * them back when it is not. Nothing else in the app writes `document.title`
 * (verified 2026-09-19: zero matches for `document.title` under `frontend/src`
 * before this file), so this hook is the single writer and there is no second
 * mechanism it can fight with.
 *
 * THE DEFAULT IS CAPTURED, NOT HARD-CODED
 * ---------------------------------------
 * `install` reads the live values out of the document at startup rather than
 * carrying its own copy of "ThreadFlow" and the favicon path. Two copies of a
 * default is how a change to index.html silently stops being reverted to: the
 * tab would keep restoring a string nobody ships any more. Capturing means the
 * restore target is whatever index.html actually says, forever.
 *
 * THE REVERT IS THE FEATURE, NOT THE CLEANUP
 * ------------------------------------------
 * In an SPA there is no page load between `/shop/acme` and `/admin`. Anything
 * written to `document.title` therefore STAYS there across a soft navigation
 * unless something puts it back. A distributor's brand sitting on an admin page
 * is the failure this unit must not ship, and it is invisible to any test that
 * only looks at the storefront -- which is why `apply` and `restore` are the
 * same code path here (`_applyFor`), chosen by one predicate, rather than an
 * apply with a cleanup bolted beside it. There is no route that gets neither.
 *
 * WHY `afterEach` AND NOT `beforeEach`
 * ------------------------------------
 * `beforeEach` runs before a navigation is committed, and this app aborts and
 * redirects navigations in several guards (`requireToken`, `requireCapability`,
 * `createStorefrontPasswordGate`). Branding a destination the visitor never
 * reached would leave the tab describing a store they were just bounced out of.
 * `afterEach` reports the final, committed destination of any redirect chain,
 * and its `failure` argument distinguishes an aborted navigation -- vue-router
 * fires `afterEach` for those too, naming the route that was NOT reached. Same
 * reasoning, and the same `failure` check, as
 * `dismissNotificationOnRouteChange`.
 *
 * WHY IT READS VUEX RATHER THAN FETCHING
 * --------------------------------------
 * `useStorefrontChrome` already GETs `getStorefrontCampaign` for the brand
 * color, store name and logo, and Unit 1 put the white-label answer on that
 * same payload deliberately so the SPA needs no second round-trip. That
 * composable commits the results (`setStoreName`, `setBrandLogoUrl`,
 * `setStorefrontWhiteLabel`); this hook reads them. A fetch here would be a
 * second source for the same fact, and the two would disagree for one paint on
 * every navigation -- the exact visible-swap this arrangement exists to avoid.
 *
 * The consequence is an ORDERING one worth stating: on the very first paint of
 * a storefront the store payload has not arrived yet, so the tab shows
 * "ThreadFlow" until it does. That is correct -- an unknown store is neither
 * entitled nor toggled on -- and the alternative (guessing from
 * the slug) would brand the tab for a store that might turn out not to be
 * entitled at all. `installStorefrontChromeWatch` closes the gap by re-applying
 * when the answer lands, so the visible behaviour is "ThreadFlow briefly, then
 * the store", never "wrong store, then right store".
 *
 * ASCII-only.
 */

/**
 * Is this route a storefront surface?
 *
 * `meta.layout === 'storefront'` is the app's own answer to that question --
 * every route in `router/groups/storefront.js` carries it (16 of them), it is
 * what `App.vue` already uses to choose the chrome, and
 * `routes-meta-layout.spec.js` pins it. Matching on the PATH instead would
 * need a list of prefixes (`/shop`, the legacy `/store`, the redirects) that
 * would drift from the route table the first time a path moved.
 *
 * Absent meta means NOT a storefront. A route that has not declared itself
 * is treated as ordinary app chrome, which is the safe direction: the failure
 * mode of guessing wrong here is a leaked brand, and the failure mode of
 * guessing the other way is an unbranded tab.
 */
function isStorefrontRoute(route) {
    return route?.meta?.layout === 'storefront';
}

/**
 * Set an attribute on a `<meta>` tag, if it is there.
 *
 * Deliberately does NOT create the tag when missing. index.html ships both
 * og tags, so an absent one means the document is not the one this app
 * serves -- a test fixture, or a future index.html that dropped them. In
 * either case synthesizing a tag would invent Open Graph data for a page whose
 * author removed it.
 *
 * `setAttribute` takes the value as TEXT. A store name is distributor-authored
 * and therefore attacker-influenced; going through the DOM API rather than
 * building a markup string means it cannot close the attribute and open a
 * `<script>`.
 */
function setMeta(property, content) {
    const tag = document.head?.querySelector(`meta[property="${property}"]`);
    if (tag) tag.setAttribute('content', content);
}

function iconLink() {
    return document.head?.querySelector('link[rel="icon"]') ?? null;
}

/**
 * Capture the values index.html ships, so they are what we restore to.
 *
 * Read once at install, before anything has had a chance to overwrite them.
 */
function captureDefaults() {
    return {
        title: document.title,
        icon: iconLink()?.getAttribute('href') ?? null,
        ogTitle:
            document.head
                ?.querySelector('meta[property="og:title"]')
                ?.getAttribute('content') ?? null,
        ogDescription:
            document.head
                ?.querySelector('meta[property="og:description"]')
                ?.getAttribute('content') ?? null,
    };
}

/**
 * The chrome a given route should show: either the store's, or the defaults.
 *
 * ONE function decides both, which is what makes a leak structurally hard: a
 * non-storefront route is not "skipped", it is actively given the ThreadFlow
 * values. There is no branch that returns without writing.
 *
 * `enabled` is the TIER flag and is the only thing consulted for permission --
 * never the presence of a logo. A store that uploaded one and then lost the
 * entitlement must render ThreadFlow again, and reading the asset as the
 * permission would let a lapsed subscription keep paid branding indefinitely.
 * That reasoning is `services/white_label.py`'s and this is the frontend half
 * of it.
 */
function chromeFor(route, store, defaults) {
    if (!isStorefrontRoute(route)) return defaults;

    // THE TITLE IS THE STORE NAME ON EVERY STOREFRONT (GH-3631, Jake
    // 2026-10-22), white-labeled or not. Only the favicon and the og pair
    // stay gated on white-label below: a non-white-label store keeps the
    // ThreadFlow icon, but its tab names the store. Format: the bare store
    // name, trimmed (PolicyPage prefixes its own section label).
    //
    // WHILE THE NAME IS UNKNOWN the title is `null`, which `applyChrome`
    // reads as "do not write". The tab keeps whatever it already shows (on a
    // cold load that is index.html's ThreadFlow) instead of flashing a wrong
    // title, and `installStorefrontChromeWatch` re-applies when the name
    // lands. An empty or whitespace-only name is the same as unknown.
    //
    // THE NAME MUST BELONG TO THIS ROUTE'S STORE. `storeName` in Vuex is
    // committed only when a store's payload lands and is never cleared by
    // navigation, so on storefront A -> B `afterEach` fires while A's name is
    // still there. The slug it was committed for (`getStoreSlug`) must equal
    // the route's `:slug`; otherwise everything store-derived (name, white-
    // label answer, icon) is treated as unknown and the tab shows the
    // ThreadFlow default until B's payload lands. If B never loads (404,
    // failed fetch, password gate) it simply stays ThreadFlow.
    const routeSlug = route?.params?.slug;
    const sameStore =
        typeof routeSlug === 'string' &&
        typeof store?.getters?.getStoreSlug === 'string' &&
        store.getters.getStoreSlug.toLowerCase() === routeSlug.toLowerCase();
    if (!sameStore) return defaults;
    const rawName = store?.getters?.getStoreName;
    const storeName = typeof rawName === 'string' ? rawName.trim() : '';
    const title = storeName || null;

    // BOTH GATES (Unit 8, 2026-09-20), now for the favicon and og pair only.
    // `enabled` is the TIER entitlement,
    // `white_label_on` the per-STORE toggle, and the icon is a store-scoped
    // surface exactly like the three footers -- so it must agree with them.
    //
    // Gating on `enabled` alone would have rebranded the browser tab of
    // every entitled store the moment white_label_002 deployed, including
    // stores that had uploaded assets under Units 1-6 but never asked to
    // switch over, while their footers correctly still read ThreadFlow.
    // A tab that says one brand and a footer that says another is a
    // half-applied white-label, which is worse than none.
    const whiteLabel = store?.getters?.getStorefrontWhiteLabel;
    if (whiteLabel?.enabled !== true || whiteLabel?.white_label_on !== true) {
        return { ...defaults, title };
    }
    if (!storeName) return { ...defaults, title: null };

    // THE FAVICON FALLBACK CHAIN (Unit 12, 2026-09-22), most specific
    // first. Jake: "For the AdventHealth store, please pull the real
    // AdventHealth favicon from https://www.adventhealth.com/ instead of
    // using the full brand image."
    //
    //   1. the store's FAVICON        -- authored for a tab: square, and
    //                                    legible at 16x16
    //   2. else the store's BRAND IMAGE -- what Unit 6 shipped, unchanged
    //   3. else the ThreadFlow default
    //
    // WHY A FAVICON FIELD AT ALL. A brand image is usually a wordmark, and
    // a wordmark squeezed into a 16x16 tab icon is unreadable. AdventHealth
    // publish a four-leaf pinwheel mark as their own favicon -- visibly not
    // their wordmark -- which is the shape this rung exists to carry.
    //
    // RUNG 2 IS DELIBERATELY LEFT IN PLACE. Removing it would regress every
    // store that has a brand image and no favicon: their tab icon would
    // revert to ThreadFlow on deploy, with nothing on screen to explain it.
    // The new field goes ABOVE the existing behaviour, not in place of it,
    // so the column is a pure opt-in.
    //
    // FALSY, NOT null-CHECKED, at each rung. The remove path writes '' (the
    // uploader clears to an empty string, not to null), and the composable
    // normalizes an unresolvable key to null -- so both "never set" and
    // "deliberately cleared" must fall through, and only a falsy test
    // catches both.
    //
    // THE TITLE DOES NOT MOVE WITH IT. The resolver reports the toggle and
    // each asset separately precisely so a missing image is representable
    // without losing the store's name, and Unit 6's arrangement (favicon
    // falls back on its own) is preserved exactly.
    //
    // RUNG 3 IS NOT A SURVIVAL OF THE SUPERSEDED RULE 3. The three footer
    // surfaces render NO icon when white-labeled without a logo, and
    // correctly so -- they have a no-icon state available. A browser tab
    // does not: it displays SOME favicon no matter what, and the
    // alternative to the default is a broken-image glyph, not an absence.
    // So "no ThreadFlow logo at all" is honored everywhere it is
    // expressible, and this surface is the one place it is not.
    const faviconUrl =
        store?.getters?.getBrandFaviconUrl ||
        store?.getters?.getBrandLogoUrl ||
        defaults.icon;

    return {
        title,
        icon: faviconUrl,
        ogTitle: storeName,
        // Mirrors index.html's "Welcome to your ThreadFlow Store" with the
        // store's name in ThreadFlow's place, so the shape of the sentence is
        // unchanged and only the brand moves.
        ogDescription: `Welcome to your ${storeName} Store`,
    };
}

function applyChrome(chrome) {
    // Assigning `document.title` sets TEXT. The browser does not parse it as
    // markup, so a store name containing tags cannot inject anything.
    if (chrome.title !== null) document.title = chrome.title;

    const link = iconLink();
    if (link && chrome.icon !== null) link.setAttribute('href', chrome.icon);

    if (chrome.ogTitle !== null) setMeta('og:title', chrome.ogTitle);
    if (chrome.ogDescription !== null) {
        setMeta('og:description', chrome.ogDescription);
    }
}

/**
 * Install the storefront browser-chrome hook.
 *
 * @param {import('vue-router').Router} router
 * @param {import('vuex').Store} store Read-only here. Needs
 *   `getStoreName`, `getBrandLogoUrl`, `getBrandFaviconUrl` (Unit 12) and
 *   `getStorefrontWhiteLabel`; a store double carrying only those is
 *   sufficient, and a store missing them degrades to always showing the
 *   defaults rather than throwing. A double predating Unit 12 that omits
 *   `getBrandFaviconUrl` degrades to the Unit 6 behaviour (the brand image),
 *   which is why the chain uses optional chaining rather than destructuring.
 * @returns {() => void} Re-applies the chrome for the router's CURRENT route.
 *   `installStorefrontChromeWatch` calls this when the store payload lands
 *   after the navigation that needed it.
 */
export function installStorefrontBrowserChrome(router, store) {
    const defaults = captureDefaults();

    const applyForCurrentRoute = () => {
        applyChrome(chromeFor(router.currentRoute?.value, store, defaults));
    };

    router.afterEach((to, from, failure) => {
        if (failure) {
            // Aborted or redirected away by a guard, so the visitor never left
            // the page they were on. vue-router still fires afterEach here,
            // naming the destination that was NOT reached -- branding it would
            // put a store's identity on a tab showing a different page. This
            // app aborts navigations in requireToken, requireCapability and
            // createStorefrontPasswordGate, so this is a live path.
            return;
        }

        applyChrome(chromeFor(to, store, defaults));
    });

    activeReapply = applyForCurrentRoute;
    return applyForCurrentRoute;
}

// The most recently installed hook's re-apply. Lets a page that temporarily
// overrides the tab title (PolicyPage's "<label> - <store>") hand control back
// on unmount WITHOUT capturing a title string: a captured string goes stale
// when the store changes underneath the page (GH-3631 gate round 1, P2).
let activeReapply = null;

/**
 * Re-apply the correct chrome for the router's CURRENT route. A no-op when
 * the hook is not installed (unit tests that mount a page in isolation).
 */
export function reapplyStorefrontChrome() {
    if (typeof activeReapply === 'function') activeReapply();
}

/**
 * Re-apply the chrome when the store payload arrives after the navigation.
 *
 * The ordering problem this closes: `afterEach` fires the instant a
 * storefront route commits, and `useStorefrontChrome` has not finished its GET
 * yet, so the white-label answer is absent and the hook correctly falls back to
 * ThreadFlow. Without this watch the tab would stay ThreadFlow for the whole
 * visit -- which is indistinguishable, on screen, from a store that is
 * correctly not entitled. That is the shape of bug that ships.
 *
 * Watching the committed STATE rather than wrapping the fetch means any path
 * that resolves a store -- the Home page, a deep link straight to a product, a
 * cart reload, anything added later -- gets the same treatment without knowing
 * this module exists.
 *
 * @param {import('vuex').Store} store A real Vuex store (needs `watch`).
 * @param {() => void} reapply The callback returned by
 *   `installStorefrontBrowserChrome`.
 */
export function installStorefrontChromeWatch(store, reapply) {
    // Guarded for the same reason `dismissNotificationOnRouteChange` guards
    // `store.subscribe`: several existing specs import `@/router` against a
    // hand-rolled store stub, and hard-requiring `watch` there would turn an
    // unrelated test's double into an import-time crash. A store without
    // `watch` simply gets no re-apply, which is the correct degradation.
    if (typeof store?.watch !== 'function') return;

    store.watch(
        (state, getters) => [
            getters.getStorefrontWhiteLabel,
            getters.getStoreName,
            getters.getStoreSlug,
            getters.getBrandLogoUrl,
            // Unit 12. Watched alongside the logo, not instead of it: on a
            // cold load the favicon commit is what lands last for a store
            // that has one, and omitting it here would leave the tab on
            // the brand image for the whole visit -- indistinguishable, on
            // screen, from a store that simply has no favicon.
            getters.getBrandFaviconUrl,
        ],
        () => reapply(),
        // Not deep: the white-label object is REPLACED wholesale by
        // `setStorefrontWhiteLabel` rather than mutated in place, so identity
        // comparison already sees every change, and a deep watch would walk it
        // on every unrelated commit for nothing.
        { deep: false }
    );
}

export default installStorefrontBrowserChrome;
